import {makeAutoObservable} from "mobx";
import {CHIP_COLORS} from "../../../../components/chip/chip";
import {DATETIME_VIEW} from "../../../../components/datetime-v2/datetime-v2";
import {formatDateTimeWithFallback} from "../../../../utilities/timezone-utilities";
import {TOOLTIP_VARIANTS} from "../../../../components/tooltip/tooltip";
import currentPortalStore from "../../../../stores/current-portal-store";
import {DRAFT_ORDER_EXCHANGE_TYPES, DRAFT_ORDER_FEE_TYPES, DRAFT_ORDER_STATE, DRAFT_ORDER_TYPE, IRmaExchangeOrder} from "../rma-types";
import CustomerCaseStore from "./customer-case-store";

interface IReservedInfo {
    tooltipText: string;
    tooltipVariant: TOOLTIP_VARIANTS;
    chipLabel: string;
    chipColor: CHIP_COLORS;
}

class RmaExchangeOrderStore {
    private _model: IRmaExchangeOrder = {} as IRmaExchangeOrder;
    
    constructor(private _customerCaseStore: CustomerCaseStore, model: IRmaExchangeOrder) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._setModel(model);
    }
    
    private _setModel(model: IRmaExchangeOrder){
        this._model = model;
    }
    
    public get orderName(): string { return this._model.order_name; }
    public get orderId(): string | null{ return this._model.order_id; }
    public get orderLink(): string | null{ return this._model._order_link; }
    public get draftOrderId(): string { return this._model.draft_order_id; }
    public get draftOrderLink(): string { return this._model._draft_order_link; }
    public get draftOrderType(): DRAFT_ORDER_TYPE { return this._model.draft_order_type; }
    public get internalState(): DRAFT_ORDER_STATE { return this._model.internal_state; }
    public get releaseDate(): string|null { return this._model.release_date; }
    public get totalBonus(): number { return this._customerCaseStore.totalIncentiveAmount; }
    public get totalAmountAtTheTimeOfDraftOrderCreation(): number { return this._model.total_amount_at_the_time_of_draft_order_creation; }
    /** This is a computed reserve until on cuatomer case level based on latest reserve_untill of product case offers */
    private get _reserveUntil(): string|null { return this._customerCaseStore.reserveUntil; }
    
    
    public getTypeText() : string {
        if(this.isExchange() && !this.isFee()) {
            return "EXCHANGE";
        }
        else {
            return 'CHARGED_FEES';
        }
    }
    
    public isExchange(): boolean {
        return DRAFT_ORDER_EXCHANGE_TYPES.includes(this._model.draft_order_type);
    }
    
    public isFee(): boolean {
        return DRAFT_ORDER_FEE_TYPES.includes(this._model.draft_order_type);
    }
    
    public isExchangeReleased(): boolean {
        return this.isExchange() && this.internalState === DRAFT_ORDER_STATE.INVOICE_PAID;
    }
    
    public orderLinkIdSuffix(): string {
        return this._didBecomeOrder() ? 'orders/' + this.orderId : 'draft_orders' + this.draftOrderId;
    }

    private get showReserveItemsIndicator(): boolean {
        return this._customerCaseStore.reserveItemCount > 0 && this.isExchange();
    }
    
    /** 
     * Implementation of this method should be aligned with "reservedItemsInfo" method  
     * in RmaListItemStore: src/pages/manage-returns/rma-list-page/stores/rma-item-store.ts
     */
    public getReservedItemsInfo(getTranslation: (key: string, scope: string, params?: string[]) => string): IReservedInfo | null {
        /** if exchange order does not have reserved items - we should not return any information */
        if (!this.showReserveItemsIndicator) {
            return null;
        }
        const pageScope = 'dashboard-returns-rma-card';
        const reserveItemsCount = this._customerCaseStore.reserveItemCount;
        
        const tzStore = currentPortalStore.setting?.timezoneStore;
        const formattedUntilDateTime = formatDateTimeWithFallback(tzStore, this._reserveUntil, DATETIME_VIEW.FULL);
        const formattedReleasedDateTime = formatDateTimeWithFallback(tzStore, this.releaseDate, DATETIME_VIEW.FULL);
        
        if (this._isReserveExpired) {
            const reservationMessage = (this.releaseDate !== null) 
                ? getTranslation(`RESERVE_PERIOD_EXPIRE_RELEASE_SINGULAR`, pageScope, [formattedUntilDateTime])
                : (reserveItemsCount > 1 ?
                    getTranslation(`RESERVED_RESTOCKED_PLURAL`, pageScope, [formattedUntilDateTime]) :
                    getTranslation(`RESERVED_RESTOCKED_SINGULAR`, pageScope, [formattedUntilDateTime])
                );
            return {
                chipColor: CHIP_COLORS.RED,
                tooltipVariant: TOOLTIP_VARIANTS.ERROR,
                chipLabel: getTranslation(`EXPIRED`, pageScope),
                tooltipText: reservationMessage
            };
        }

        if (this._isReserveReleased) {
            return {
                chipColor: CHIP_COLORS.GREEN,
                tooltipVariant: TOOLTIP_VARIANTS.SUCCESS,
                chipLabel: getTranslation(`RELEASED`, pageScope),
                tooltipText: reserveItemsCount > 1 ?
                    getTranslation(`DRAFT_RELEASED_PAST_PLURAL`, pageScope, [formattedReleasedDateTime]) :
                    getTranslation(`DRAFT_RELEASED_PAST_SINGULAR`, pageScope, [formattedReleasedDateTime])
            };
        }

        /** If reservation was not expired and not released - show that items are reserved and time untill */
        return {
            chipColor: CHIP_COLORS.BLUE,
            tooltipVariant: TOOLTIP_VARIANTS.INFO,
            chipLabel: getTranslation(`RESERVED`, pageScope),
            tooltipText: reserveItemsCount > 1 ? 
                getTranslation(`ITEM_IN_DRAFT_ORDER_IS_RESERVED_UNTIL_PLURAL`, pageScope, [formattedUntilDateTime]) :
                getTranslation(`ITEM_IN_DRAFT_ORDER_IS_RESERVED_UNTIL_SINGULAR`, pageScope, [formattedUntilDateTime])
        };
    }

    private get _isReserveExpired(): boolean {
        if (this._reserveUntil === null) {
            return false;
        }

        /** if already released - check if released before or after reservation ended */
        if (this.releaseDate !== null) {
            return (new Date(this._reserveUntil)).getTime() < (new Date(this.releaseDate)).getTime();    
        }

        /** if not yet released - check if reservation time is not in past */
        return (new Date(this._reserveUntil)).getTime() < (new Date()).getTime();
    }

    private get _isReserveReleased(): boolean {
        return !this._isReserveExpired && this.releaseDate !== null;
    }
    
    private _didBecomeOrder(){
        return this._model.order_id !== null;
    }
}

export { RmaExchangeOrderStore as default };
